import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type Query, type QueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  AnyListQuery,
  ListAggregateKind,
  ListColumnOption,
  ListColumnType,
  ListCompareOperator,
  ListCondition,
  ListFilterGroup,
  ListMetric,
  ListMetricSeries,
  ListMoneyValue,
  ListQuery,
  ListSort,
  SuspendedFilter,
} from '@till-erp/platform/lists';
import { LIST_QUERY_VERSION } from '@till-erp/platform/lists';
import { apiFetch, newIdempotencyKey, type TokenGetter } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';

/**
 * Schnittstelle des Listen-Baukastens (`/api/v1/lists/{liste}/…`, Story 1.20). Spaltenschlüssel kommen nur vom
 * Server; die Web-App schickt sie unverändert zurück (nie SQL oder Spaltennamen der Datenbank).
 */

export interface ListColumnView {
  key: string;
  label: string;
  group: string;
  type: ListColumnType;
  filterable: boolean;
  sortable: boolean;
  defaultVisible: boolean;
  width?: number;
  options: ListColumnOption[] | null;
  distinctOptions?: boolean;
  subtitle?: string;
  /** Wert von `subtitle` auch bei leerer Zelle zeigen (Story 5.84: „– · Gewicht fehlt“). */
  subtitleWhenEmpty?: boolean;
  /** Kurzwort vor dem Wert, wenn die Ja/Nein-Spalte `column` wahr ist (Story 7.8, z. B. „Express“). */
  flag?: { column: string; label: string };
  alwaysVisible?: boolean;
  /** Zahl immer mit Vorzeichen („+20 Stück“, Story 5.38). */
  signed?: boolean;
  align: 'left' | 'right';
  /** Nur Beträge: vorhandene Währungen der Liste (Auswahl im Betragsfilter). */
  currencies?: string[] | null;
  /** Kennzahl-Arten dieser Spalte (Kachel `{ column, aggregate }`, Story 1.32). */
  aggregates?: Exclude<ListAggregateKind, 'count'>[];
  /** Angebotene Spaltenvergleiche (`compare_column`): `<diese Spalte> <op> <column>`. */
  comparisons?: { op: ListCompareOperator; column: string }[];
  /** Von der Schnellsuche durchsucht. */
  searchable?: boolean;
  /** Spalte mit Standard aus den Einstellungen: Zusatz „(Standard)“, wenn die Zeile ihn in `defaulted` meldet. */
  fallbackLabel?: string;
  /** „Gruppieren nach …“ bietet die Spalte an (Story 1.33). */
  groupable?: boolean;
  /** Kennzahl aus dem Tageslauf dieses Moduls (Stand über `/metrics-status`, Story 1.33). */
  refresh?: string;
}

/** Beziehungsfilter der Liste (Story 1.33): Pillentext und spätere Eingabe „Gekauft …“. */
export interface ListRelationInfo {
  key: string;
  label: string;
  hasLabel: string;
  hasNotLabel: string;
  quantity: boolean;
  targets: { key: string; label: string; list?: string }[];
}

export interface ListInfo {
  key: string;
  label: string;
  rowNoun: { one: string; many: string };
  defaultSort: ListSort;
  titleColumn: string;
  defaultColumns: string[];
  emptyText: string;
  noMatchText: string;
  /** Stammdaten-Liste mit Massenbearbeitung (Story 1.21): „Bearbeiten“ in der Sammel-Aktionsleiste. */
  massEditable: boolean;
  /** Start-Kacheln des Kennzahl-Kopfs („Standard wiederherstellen“, Story 1.32). */
  defaultMetrics?: ListMetric[];
  /** Beziehungsfilter (Story 1.33). */
  relations?: ListRelationInfo[];
  /** Zeiträume der Kennzahlen in Tagen (Beziehungsfilter „letzte 30 Tage“ …, Story 6.19). */
  metricPeriods?: { short: number; medium: number; long: number };
  /** Stichtag-Liste (Story 5.44): Werte zum gewählten Datum (`asOf` an jeder Anfrage). */
  asOf?: { label: string };
}

/**
 * Stichtag der Liste (Story 5.44, ISO-Datum): `ListView asOf` setzt ihn; Zeilen, Kacheln, Gruppen und „Nächste“ schicken
 * ihn als `asOf` mit und tragen ihn im Abfrage-Schlüssel (Wechsel = neu laden). `undefined` = keine Stichtag-Liste.
 */
export const ListAsOfContext = createContext<string | undefined>(undefined);

/** Stichtag der umgebenden Liste (siehe `ListAsOfContext`). */
export function useListAsOf(): string | undefined {
  return useContext(ListAsOfContext);
}

/** `asOf` für den Anfrage-Body, nur wenn gesetzt. */
const asOfBody = (asOf: string | undefined) => (asOf ? { asOf } : {});

export interface ListColumnsResponse {
  list: ListInfo;
  columns: ListColumnView[];
}

export interface ViewColumn {
  column: string;
  width?: number;
}

/**
 * Spalten, Filter und Sortierung einer Ansicht (Arbeitsstand). Filter als Version 1 oder 2 (Story 1.32: die Oberfläche
 * schickt die kleinste gleichwertige Beschreibung, `compactListQuery`); Kennzahl-Kacheln gehören zur Ansicht.
 */
export interface ViewState {
  columns: ViewColumn[];
  query: AnyListQuery;
  metrics: ListMetric[];
  /** Gruppieren nach dieser Spalte (Story 1.33); `null` = nicht gruppiert. */
  groupBy: string | null;
}

/** Hinweis zu einer ausgesetzten Kachel (Spalte oder Kennzahl-Art entfallen). */
export interface SuspendedMetric {
  index: number;
  column: string;
  text: string;
}

export interface ListViewItem extends ViewState {
  id: string;
  list: string;
  name: string;
  shared: boolean;
  showCount: boolean;
  position: number;
  version: number;
  updatedAt: string;
  publishedAt: string | null;
  missingColumns: string[];
  suspendedFilters: SuspendedFilter[];
  suspendedMetrics?: SuspendedMetric[];
  /** Gruppieren ausgesetzt (Spalte entfallen): Hinweis statt still entfernt. */
  groupBySuspended?: string | null;
  /** Die angemeldete Person beobachtet diese Ansicht (Story 1.34). */
  watched?: boolean;
  /** Seiten-Einstellungen der Ansicht (Story 10.18, z. B. Rechenleiste des Bestellvorschlags); `null` = keine. */
  pageParams?: ViewPageParams | null;
}

/** Seiten-Einstellungen einer Ansicht (Story 10.18): kleine Schlüssel-Wert-Liste, nur die Seite selbst deutet sie. */
export type ViewPageParams = Record<string, string | number | boolean>;

/** Reiterzahl einer freigegebenen Ansicht mit „Zähler zeigen“ (`GET …/view-counts`, Story 7.21). */
export interface ListViewCount {
  viewId: string;
  count: number;
  /** `false` = mehr als `countThreshold` („mehr als 10.000“). */
  countExact: boolean;
  countThreshold: number;
}

/** Neue Zeilen einer beobachteten Ansicht (Klick in der Glocke, Story 1.34). */
export interface ListWatchHit {
  id: string;
  list: string;
  viewId: string;
  viewName: string;
  asOf: string;
  rowCount: number;
  keys: string[];
}

export interface ListPreference {
  defaultViewId: string | null;
  lastViewId: string | null;
  draft: (Omit<ViewState, 'metrics' | 'groupBy'> & { metrics?: ListMetric[]; groupBy?: string | null; viewId: string }) | null;
}

export interface ListViewsResponse {
  shared: ListViewItem[];
  own: ListViewItem[];
  preference: ListPreference;
}

export interface ListRow {
  key: string;
  values: Record<string, unknown>;
  /** Spalten, deren Wert der Standard aus den Einstellungen ist (Zusatz „(Standard)“, Story 1.32). */
  defaulted?: string[];
  /** Spalten, deren Zelle den Wert des Hauptartikels zeigt (grau mit „· geerbt“, Story 5.57). */
  inherited?: string[];
  /** Warnung je Spalte (Story 9.40, z. B. „läuft in 3 Tagen ab“): Zelle gelb mit Formmarke und Satz für Bildschirmleser. */
  warnings?: Record<string, string>;
  /** Stelle der Zeile in ihrer Abfrage beim Laden (Server-Cursor); für „nächste Zeile ab hier“ (`fetchListNext`, Story 7.20). */
  position?: string;
  /** Ticket 1.270 Änderung 3: Packeinheit des Artikels der Zeile für Mengenzellen; fehlt = „Stück“. */
  packUnit?: { packUnit: string | null; packUnitPlural: string | null };
}

/**
 * Vorschaubild einer Bild-Spalte (Typ `image`): Zeile, Schlüssel der Spalte (ihr Wert z. B. die Bildversion), Dichte
 * („Kompakt“ 32 px, sonst 44 px) und Text der Titelspalte (Name für Bildbeschreibung und Knopf). Die Seite der Liste weiß,
 * woher das Bild kommt (Story 5.24).
 */
export type RenderImage = (row: ListRow, column: string, compact: boolean, title: string) => ReactNode;

/** Eigene Tabellenzelle (Story 10.11): `undefined` = normale Zelle. */
export type RenderCell = (row: ListRow, column: string) => ReactNode | undefined;

export interface ListRowsResponse {
  rows: ListRow[];
  nextCursor: string | null;
  total: number;
  totalExact?: boolean;
  countThreshold?: number;
  suspended: SuspendedFilter[];
  sort: ListSort;
  columns: string[];
}

const PAGE_SIZE = 50;

/** Antwort von `POST …/next` (Story 7.20): nächste passende Zeile der Ansicht, frisch vom Server. */
export interface ListNextResponse {
  /** `null` = keine (bei `truncated` nur unter den gezeigten Gruppen gesucht). */
  row: ListRow | null;
  /** Gruppierte Ansicht: Gruppe der gefundenen Zeile. */
  group: ListRowsGroup | null;
  /** Mehr Gruppen als gezeigt: die übrigen wurden nicht durchsucht. */
  truncated: boolean;
}

/**
 * Nächste Zeile der Ansicht (Spalten, Filter, Suche, Sortierung) mit dem Wert `match` nach der Stelle von `after`, sonst
 * von oben; gruppiert ab der Herkunftsgruppe `group` über alle gezeigten Gruppen. `position`: Stelle von `after` vor der
 * Aktion (`ListRow.position`), damit eine geänderte Sortierspalte die Suche nicht verschiebt. Ohne Cache (immer der aktuelle Stand).
 */
export function fetchListNext(
  getToken: Parameters<typeof apiFetch>[0],
  list: string,
  columns: readonly string[],
  query: AnyListQuery,
  after: string,
  match: ListRowsGroup,
  group: ListRowsGroup | null,
  position?: string,
  asOf?: string,
): Promise<ListNextResponse> {
  return apiFetch<ListNextResponse>(getToken, `/api/v1/lists/${list}/next`, {
    method: 'POST',
    body: { columns, query, after, match, ...(group ? { group } : {}), ...(position ? { position } : {}), ...asOfBody(asOf) },
  });
}

/** Abfrage-Schlüssel einer Liste; `asOf` (Stichtag, Story 5.44) am Ende von Zeilen, Kacheln und Gruppen. */
export function listKeys(list: string, asOf?: string) {
  const day = asOf ?? null;
  return {
    columns: ['list-columns', list] as const,
    views: ['list-views', list] as const,
    // Unter dem Schlüssel der Ansichten: wer die Ansichten neu lädt, lädt die Reiterzahlen mit.
    viewCounts: ['list-views', list, 'counts', day] as const,
    rows: (columns: readonly string[], query: AnyListQuery, group?: ListRowsGroup) => ['list-rows', list, columns, query, group ?? null, day] as const,
    all: ['list-rows', list] as const,
    // Nach einer Datenmutation gehören Zeilen, Kopf-Kennzahlen und Gruppensummen zum selben Stand.
    // Die Reiterzahlen (`viewCounts`) gehören dazu: sonst zeigen inaktive Reiter nach einer Änderung alte Zahlen.
    data: {
      predicate: (query: Query) =>
        (['list-rows', 'list-metrics', 'list-groups'].includes(String(query.queryKey[0])) ||
          (query.queryKey[0] === 'list-views' && query.queryKey[2] === 'counts')) &&
        query.queryKey[1] === list,
    },
    metrics: (query: AnyListQuery, metrics: readonly ListMetric[], wait: 'short' | 'long') => ['list-metrics', list, query, metrics, wait, day] as const,
    groups: (query: AnyListQuery, metrics: readonly ListMetric[], groupBy: string, wait: 'short' | 'long') =>
      ['list-groups', list, query, metrics, groupBy, wait, day] as const,
    status: ['list-metrics-status', list] as const,
  };
}

/**
 * Spalten einer Liste. `values: 'fixed'` lässt die vorhandenen Werte aus den Daten weg (Filter-Auswahlen, Währungen):
 * schnell für den ersten Bildaufbau – die ListView holt `full` erst, wenn ein Filter angefasst wird (Lasttest
 * orders-browser: die Werte kosteten bei 100 000 Aufträgen 0,5–1 s vor der ersten Zeile). Ohne Angabe wie bisher `full`.
 */
export function useListColumns(list: string, values: 'full' | 'fixed' = 'full', enabled = true) {
  const { getToken } = useSession();
  return useQuery({ ...listColumnsQuery(getToken, list, values), enabled });
}

function listColumnsQuery(getToken: TokenGetter, list: string, values: 'full' | 'fixed') {
  return {
    queryKey: values === 'full' ? listKeys(list).columns : [...listKeys(list).columns, 'fixed'],
    queryFn: () => apiFetch<ListColumnsResponse>(getToken, `/api/v1/lists/${list}/columns${values === 'fixed' ? '?values=fixed' : ''}`),
    staleTime: 60_000,
  };
}

export function useListViews(list: string) {
  const { getToken } = useSession();
  return useQuery(listViewsQuery(getToken, list));
}

function listViewsQuery(getToken: TokenGetter, list: string) {
  return {
    queryKey: listKeys(list).views,
    queryFn: () => apiFetch<ListViewsResponse>(getToken, `/api/v1/lists/${list}/views`),
    // Kurz frisch: Vorab geladene Ansichten (`prefetchListStart`) gelten beim Einhängen der Liste, ohne zweite Abfrage.
    // Eigene Änderungen laden die Ansichten ohnehin neu (invalidate).
    staleTime: 10_000,
  };
}

/**
 * Ansichten und Spalten (ohne Auswahlwerte) einer Liste vorab laden – beim Start zugleich mit der Anmeldung (`/me`),
 * wenn die aufgerufene Seite eine Liste ist (`staticData.list` der Route). Die Liste findet beides dann schon vor und
 * fragt ihre Zeilen sofort ab (Lasttest orders-browser, Erstansicht). Fehler zeigt die Liste selbst beim Laden.
 */
export function prefetchListStart(client: QueryClient, getToken: TokenGetter, list: string): void {
  void client.prefetchQuery(listViewsQuery(getToken, list));
  void client.prefetchQuery(listColumnsQuery(getToken, list, 'fixed'));
}

/**
 * Reiterzahlen (Story 7.21): eigene Abfrage, damit Ansichten und erste Zeilen nicht auf das Zählen warten; die Zahlen
 * erscheinen, sobald sie da sind.
 */
export function useListViewCounts(list: string, scope?: ListCondition | ListFilterGroup) {
  const { getToken } = useSession();
  const asOf = useListAsOf();
  // Story 5.100: feste Bedingung der Seite (z. B. Reiter der Artikelliste) zählt mit; im Schlüssel, damit je Reiter eigene Zahlen.
  const scopeText = scope ? JSON.stringify(scope) : null;
  const params = new URLSearchParams();
  if (asOf) params.set('asOf', asOf);
  if (scopeText) params.set('scope', scopeText);
  const search = params.size > 0 ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...listKeys(list, asOf).viewCounts, scopeText] as const,
    queryFn: async () =>
      new Map((await apiFetch<{ counts: ListViewCount[] }>(getToken, `/api/v1/lists/${list}/view-counts${search}`)).counts.map((c) => [c.viewId, c])),
  });
}

/**
 * Genaue Zeilenzahl einer Liste mit optionalen Bedingungen (Ticket 1.272: Zähler und Kacheln über Listen wie
 * Auftragsentwürfe und „Wartet auf Ware“). Nur der vorhandene Lese-Endpunkt `/lists/<list>/rows` mit `limit: 1`; eine
 * Zahl, die der Server nicht genau bestätigt (ausgesetzter Filter, Schätzung), gilt als unbekannt (`null`).
 */
export function useListTotal(list: string, titleColumn: string, filters: ListQuery['filters'], key: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: [...listKeys(list).all, 'gesamt', key, titleColumn, filters] as const,
    queryFn: async () => {
      const r = await apiFetch<ListRowsResponse>(getToken, `/api/v1/lists/${list}/rows`, {
        method: 'POST',
        headers: { Prefer: 'count=exact' },
        body: { columns: [titleColumn], query: { version: LIST_QUERY_VERSION, filters, sort: null }, limit: 1 },
      });
      // Wie `fetchOrdersToday`: ein ausgesetzter Filter, ein ungenauer oder unplausibler Gesamtwert bestätigt keine Zahl.
      if (!r || !Array.isArray(r.suspended) || r.suspended.length > 0 || r.totalExact === false || !Number.isSafeInteger(r.total) || r.total < 0) return null;
      return r.total;
    },
    refetchInterval: 30_000,
    retry: false,
  });
}

/** Zeilen seitenweise; während des Ladens bleibt die alte Tabelle stehen (EXPERIENCE §4.9 „Leistung“). */
/** Zeilen genau einer Gruppe (Story 1.33): Gruppier-Spalte und Wert wie in `/groups`. */
export interface ListRowsGroup {
  column: string;
  value: string | boolean | null;
}

export function useListRows(list: string, columns: readonly string[], query: AnyListQuery, enabled: boolean, group?: ListRowsGroup) {
  const { getToken } = useSession();
  const asOf = useListAsOf();
  return useInfiniteQuery({
    queryKey: listKeys(list, asOf).rows(columns, query, group),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiFetch<ListRowsResponse>(getToken, `/api/v1/lists/${list}/rows`, {
        method: 'POST',
        body: { columns, query, cursor: pageParam, limit: PAGE_SIZE, ...(group ? { group } : {}), ...asOfBody(asOf) },
      }),
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });
}

/**
 * Keys der Zeilen, die eine Liste gerade zeigt (Story 7.39): aus dem Zwischenspeicher der aktiven Zeilen-Abfragen (alle
 * geladenen Seiten und Gruppen), ohne Doppelte, höchstens `max`. Kein eigener Abruf.
 */
export function loadedListRowKeys(queryClient: QueryClient, list: string, max: number): string[] {
  const keys = new Set<string>();
  for (const query of queryClient.getQueryCache().findAll({ queryKey: listKeys(list).all, type: 'active' })) {
    const data = query.state.data as InfiniteData<ListRowsResponse> | undefined;
    for (const page of data?.pages ?? []) {
      for (const row of page.rows) {
        if (keys.size >= max) return [...keys];
        keys.add(row.key);
      }
    }
  }
  return [...keys];
}

/** Wert einer Kachel: Zahl, leer (z. B. Nenner 0) oder Beträge je Währung (nie über Währungen addiert). */
export type MetricValue = number | null | ListMoneyValue[];

/** Teilwert einer Kachel „Aufteilung“ (Story 4.23). */
export interface ListMetricPartResult {
  aggregate: ListAggregateKind;
  column: string | null;
  label: string;
  type: ListColumnType;
  status: 'ok' | 'pending';
  value: MetricValue;
}

export interface ListMetricResult {
  index: number;
  /** `split` = Kachel „Aufteilung“ mit Teilwerten in `parts` (Story 4.23). */
  aggregate: ListAggregateKind | 'split';
  column: string | null;
  label: string;
  type: ListColumnType;
  /** `pending`: langsamer als die Zeitgrenze („wird berechnet …“) – mit `wait: 'long'` nachladen. */
  status: 'ok' | 'pending';
  value: MetricValue;
  /** Nur Aufteilung: Teilwerte (Anteile in ganzen Prozent). */
  parts?: ListMetricPartResult[];
  /** Hinweiszeile unter der Kachel, z. B. „seit Umstieg am 01.02.2027“. */
  note?: string;
  /** „Anzahl mit Merkmal“ ist ein Anteil an den Zeilen (Server, Ticket 1.294) – nur dann die Leiste „X von N“. */
  share?: true;
  /** Satz unter „–“ bei leerer Summe (Server, Ticket 1.294); `{n}` = Zeilenzahl der Liste zum selben Filter. */
  emptyNote?: { one: string; many: string };
  /** Verlauf vom Server (Ticket 1.297): Monats- bzw. Tagesreihe zum aktuellen Filter; nie im Browser gerechnet. */
  series?: ListMetricSeries;
  /** Ticket 1.270: Einheit einer Mengen-Kachel aus der Packeinheit der Artikel; fehlt = „Stück“. */
  unit?: ListMetricUnit;
}

/**
 * Ticket 1.270: Einheit einer Mengen-Kachel vom Server. `pack`: alle Zeilen in derselben Packeinheit. `mixed`: verschiedene
 * Einheiten – nie eine Summe in einer geratenen Einheit; `parts` = Summe je Einheit (Packeinheiten zuerst, „Stück“ = beide
 * `null` zuletzt), fehlt bei mehr als 3 Einheiten oder bei Ø/Min/Max.
 */
export type ListMetricUnit =
  | { kind: 'pack'; packUnit: string | null; packUnitPlural: string | null }
  | { kind: 'mixed'; parts?: { packUnit: string | null; packUnitPlural: string | null; value: number }[] };

export interface ListMetricsResponse {
  metrics: ListMetricResult[];
  suspended: SuspendedFilter[];
  suspendedMetrics: SuspendedMetric[];
}

/**
 * Kennzahl-Kopf (Story 1.32, EXPERIENCE §4.9): Kacheln zum aktuellen Filter. Erst mit Zeitgrenze (`short`); meldet
 * eine Kachel `pending`, lädt eine zweite Abfrage ohne Grenze (`long`) nach. Die Tabelle wartet nie darauf.
 */
export function useListMetrics(list: string, query: AnyListQuery, metrics: readonly ListMetric[], enabled: boolean) {
  const { getToken } = useSession();
  const asOf = useListAsOf();
  const fetchWith = (wait: 'short' | 'long') => () =>
    apiFetch<ListMetricsResponse>(getToken, `/api/v1/lists/${list}/metrics`, { method: 'POST', body: { query, metrics, wait, ...asOfBody(asOf) } });
  const short = useQuery({
    queryKey: listKeys(list, asOf).metrics(query, metrics, 'short'),
    queryFn: fetchWith('short'),
    enabled: enabled && metrics.length > 0,
    placeholderData: keepPreviousData,
  });
  const pending = !short.isPlaceholderData && (short.data?.metrics.some((m) => m.status === 'pending') ?? false);
  const long = useQuery({
    queryKey: listKeys(list, asOf).metrics(query, metrics, 'long'),
    queryFn: fetchWith('long'),
    enabled: enabled && pending,
  });
  return { short, long, pending };
}

/** Eine Gruppe (Story 1.33): Wert wie im Filter, Beschriftung, Zahl der Zeilen und die Kacheln der Ansicht für sie. */
export interface ListGroupResult {
  value: string | boolean | null;
  label: string;
  count: number;
  metrics: ListMetricResult[];
}

export interface ListGroupsResponse {
  column: string;
  groups: ListGroupResult[];
  truncated: boolean;
  suspended: SuspendedFilter[];
  suspendedMetrics: SuspendedMetric[];
}

/**
 * Gruppen mit Zwischensummen (Story 1.33, EXPERIENCE §4.9 „Gruppieren“): dieselbe Abfrage wie die Tabelle, die Kacheln
 * der Ansicht je Gruppe. Wie der Kennzahl-Kopf erst mit Zeitgrenze, dann ohne nachladen.
 */
export function useListGroups(list: string, query: AnyListQuery, metrics: readonly ListMetric[], groupBy: string | null) {
  const { getToken } = useSession();
  const asOf = useListAsOf();
  const fetchWith = (wait: 'short' | 'long') => async () => ({
    ...await apiFetch<ListGroupsResponse>(getToken, `/api/v1/lists/${list}/groups`, { method: 'POST', body: { query, metrics, groupBy, wait, ...asOfBody(asOf) } }),
    // Der Platzhalter darf seine Werte nur mit der Abfrage dieser Antwort weiter anzeigen.
    requestQuery: query,
  });
  const short = useQuery({
    queryKey: listKeys(list, asOf).groups(query, metrics, groupBy ?? '', 'short'),
    queryFn: fetchWith('short'),
    enabled: groupBy !== null,
    placeholderData: keepPreviousData,
  });
  const pending = !short.isPlaceholderData && (short.data?.groups.some((g) => g.metrics.some((m) => m.status === 'pending')) ?? false);
  const long = useQuery({
    queryKey: listKeys(list, asOf).groups(query, metrics, groupBy ?? '', 'long'),
    queryFn: fetchWith('long'),
    enabled: groupBy !== null && pending,
  });
  return { short, long };
}

/** Stand eines Tageslaufs der Kennzahlen (Story 1.33). */
export interface ListMetricsRefresh {
  module: string;
  label: string;
  columns: string[];
  asOf: string | null;
  refreshedAt: string | null;
  stale: boolean;
}

/** Stand der Kennzahlen aus dem Tageslauf; nur für Listen mit solchen Spalten (sonst kein Aufruf). */
export function useListMetricsStatus(list: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: listKeys(list).status,
    queryFn: () => apiFetch<{ refreshes: ListMetricsRefresh[] }>(getToken, `/api/v1/lists/${list}/metrics-status`),
    enabled,
    staleTime: 60_000,
  });
}

/** Schreibende Aufrufe der Ansichten (je Aufruf ein neuer Idempotency-Key). */
export function useListWrites(list: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMemo(() => {
    const base = `/api/v1/lists/${list}`;
    const write = <T,>(method: string, path: string, body?: unknown) =>
      apiFetch<T>(getToken, `${base}${path}`, { method, body, idempotencyKey: newIdempotencyKey() });
    return {
      refresh: () => queryClient.invalidateQueries({ queryKey: listKeys(list).views }),
      create: (input: ViewState & { name: string; basedOnViewId?: string; pageParams?: ViewPageParams | null }) => write<ListViewItem>('POST', '/views', input),
      update: (view: ListViewItem, changes: Partial<ViewState> & { name?: string; pageParams?: ViewPageParams | null }) =>
        write<ListViewItem>('PUT', `/views/${view.id}`, { version: view.version, ...changes }),
      updateShared: (view: ListViewItem, changes: Partial<ViewState> & { name?: string; showCount?: boolean; pageParams?: ViewPageParams | null }) =>
        write<ListViewItem>('PUT', `/views/${view.id}/shared`, { version: view.version, ...changes }),
      remove: (view: ListViewItem) => write<{ id: string; deleted: true }>('DELETE', `/views/${view.id}?version=${view.version}`),
      publish: (view: ListViewItem, showCount?: boolean) =>
        write<ListViewItem>('POST', `/views/${view.id}/publish`, { version: view.version, ...(showCount === undefined ? {} : { showCount }) }),
      unpublish: (view: ListViewItem) => write<ListViewItem>('POST', `/views/${view.id}/unpublish`, { version: view.version }),
      preference: (changes: Partial<ListPreference>) => write<ListPreference>('PUT', '/preference', changes),
      watch: (view: ListViewItem) => write<ListViewItem>('PUT', `/views/${view.id}/watch`),
      unwatch: (view: ListViewItem) => write<ListViewItem>('DELETE', `/views/${view.id}/watch`),
    };
  }, [getToken, queryClient, list]);
}

/** Treffer einer beobachteten Ansicht (Story 1.34); ohne Kennung nichts. */
export function useListWatchHit(hitId: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['list-watch-hit', hitId],
    queryFn: () => apiFetch<ListWatchHit>(getToken, `/api/v1/lists/watch-hits/${hitId}`),
    enabled: hitId !== null,
    staleTime: Infinity,
    retry: false,
  });
}

/** Ton einer Zeile bzw. Karte: gedämpft (Story 5.100) oder hervorgehoben nach einem Sprung (Ticket 1.302). */
export type ListRowTone = 'muted' | 'spotlight';
